import { toLocalIsoDateString } from "./formatDate"

export const STORAGE_KEY = "tasbeeh"    

/** 1 crore 25 lakh (1,25,00,000) — max daily count or goal limit */
export const MAX_COUNTER_LIMIT = 12_500_000

export const MAX_COUNTER_LIMIT_MESSAGE =
 "Count or limit cannot exceed 1 crore 25 lakh (1,25,00,000)."

export const createId = () => {
 if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
 return crypto.randomUUID()
 }
 return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

/** ISO timestamp for last-selected stamping. */
export const nowIso = () => new Date().toISOString()

export const createDefaultCounter = () => ({
 id: createId(),
 name: "Default Counter",
 type: "daily",
 limit: 11,
 days: 1,
 perDayCount: 11,
 currentCount: 0,
 rounds: 0,
 spentTime: 0,
 spentDays: 0,
 totalSpentTime: 0,
 completedCount: 0,
 status: "Pending",
 isDisabled: false,
 history: [],
 createdAt: toLocalIsoDateString(),
 lastSelectedAt: null
})

/** Call when user selects a counter as active (list tap, new counter, etc.). */
export const stampLastSelected = (counters, counterId) =>
 counters.map(c => (c.id === counterId ? { ...c, lastSelectedAt: nowIso() } : c))

export const normalizeCounter = (counter) => {
 const rest = { ...counter }
 delete rest.lastUpdatedAt

 let createdAt = rest.createdAt
 if (!createdAt || !/^\d{4}-\d{2}-\d{2}/.test(String(createdAt))) {
 const fromHistory = (rest.history || [])
 .map((e) => e.date)
 .filter((d) => d && /^\d{4}-\d{2}-\d{2}/.test(String(d)))
 .sort()[0]
 createdAt = fromHistory || toLocalIsoDateString()
 }

 const isGoal = rest.type === "goal"
 const limitN = Number(rest.limit) || 0
 const daysN = Math.max(1, Number(rest.days) || 1)
 return {
 ...rest,
 createdAt,
 lastSelectedAt: typeof rest.lastSelectedAt === "string" ? rest.lastSelectedAt : null,
 completedCount: typeof rest.completedCount === "number"
 ? rest.completedCount
 : (rest.rounds || 0) * (rest.perDayCount || 0) + (rest.currentCount || 0),
 ...(isGoal
 ? {
 perDayCount: computeGoalPerDayCount(limitN, daysN),
 goalDistinctDates: Array.isArray(rest.goalDistinctDates) ? rest.goalDistinctDates : [],
 lastGoalRoundCompleteDate:
 typeof rest.lastGoalRoundCompleteDate === "string" ? rest.lastGoalRoundCompleteDate : null
 }
 : {})
 }
}

export const getInitialCounterState = () => {
 const defaultCounter = createDefaultCounter()

 try {
 const raw = localStorage.getItem(STORAGE_KEY)
 if (!raw) {
 return { counters: [defaultCounter], activeCounterId: defaultCounter.id }
 }

 const parsed = JSON.parse(raw)
 const storedCounters = Array.isArray(parsed?.counters)
 ? parsed.counters.map(normalizeCounter)
 : []

 if (storedCounters.length === 0) {
 return { counters: [defaultCounter], activeCounterId: defaultCounter.id }
 }

 const hasActive = storedCounters.some(c => c.id === parsed?.activeCounterId)
 return {
 counters: storedCounters,
 activeCounterId: hasActive ? parsed.activeCounterId : storedCounters[0].id
 }
 } catch {
 return { counters: [defaultCounter], activeCounterId: defaultCounter.id }
 }
}

/**
 * Counts per round for a goal: limit ÷ days (must divide evenly — see validateCounterInput).
 * Examples: 50÷5 → 10 in 5 rounds; 33÷3 → 11 in 3 rounds.
 */
export const computeGoalPerDayCount = (limit, days) => {
 const d = Math.max(1, Math.floor(Number(days)) || 1)
 const l = Math.max(1, Math.floor(Number(limit)) || 1)
 if (l % d === 0) return Math.max(1, l / d)
// Legacy / imported counters only (new goals must pass validation)
 return Math.max(1, Math.ceil(l / d))
}

/** True when every round can be the same size (limit is a multiple of days). */
export const isGoalEvenlyDivisible = (counter) => {
 if (!counter || counter.type !== "goal") return false
 const l = Math.floor(Number(counter.limit)) || 0
 const d = Math.max(1, Math.floor(Number(counter.days)) || 1)
 return l > 0 && d > 0 && l % d === 0
}

export const validateCounterInput = ({ type, name, limit, days }) => {
 if (!name?.trim()) return "Counter name is required."

 if (type === "daily") {
 if (!limit || limit <= 0) return "Daily count must be greater than 0."
 if (limit > MAX_COUNTER_LIMIT) return MAX_COUNTER_LIMIT_MESSAGE
 return ""
 }

 if (!limit || !days) return "Please fill all required fields."
 if (limit > MAX_COUNTER_LIMIT) return MAX_COUNTER_LIMIT_MESSAGE
 if (days <= 0) return "Total days must be greater than 0."
 if (days > limit) return "Total days cannot be more than limit."
 if (limit % days !== 0) {
 return "Limit must divide evenly by total days (e.g. 50 with 5 days → 10 per round in 5 rounds; 33 with 3 → 11 per round in 3)."
 }
 return ""
}

export const createDailyCounter = ({ name, dailyCount }) => ({
 id: createId(),
 name,
 type: "daily",
 limit: dailyCount,
 days: 1,
 perDayCount: dailyCount,
 currentCount: 0,
 rounds: 0,
 spentTime: 0,
 spentDays: 0,
 totalSpentTime: 0,
 completedCount: 0,
 status: "Pending",
 isDisabled: false,
 history: [],
 createdAt: toLocalIsoDateString(),
 lastSelectedAt: null
})

export const createGoalCounter = ({ name, limit, days }) => {
 const perDayCount = computeGoalPerDayCount(limit, days)
 return {
 id: createId(),
 name,
 type: "goal",
 limit,
 days,
 perDayCount,
 currentCount: 0,
 rounds: 0,
 spentTime: 0,
 spentDays: 0,
 totalSpentTime: 0,
 completedCount: 0,
 status: "Pending",
 isDisabled: false,
 history: [],
 createdAt: toLocalIsoDateString(),
 lastSelectedAt: null,
/** Unique local calendar dates (YYYY-MM-DD) on which the user counted toward this goal */
 goalDistinctDates: [],
/** Last local date when a goal "round" (batch) was completed — for same-day extra rounds */
 lastGoalRoundCompleteDate: null
 }
}

/**
 * Counts needed in *this* round before the round-complete modal (same as perDayCount for valid goals).
 *
 * Important: do NOT use (limit - completedCount) to shrink the batch mid-round — completedCount
 * already includes taps in the current round, so "remaining to goal" would wrongly become 6 and
 * trigger a "round" at 6 instead of 11. For evenly divisible goals, every round is exactly
 * limit/days counts until the goal ends on the last tap.
 */
export const getGoalRoundTarget = (counter) => {
 if (!counter || counter.type !== "goal") return 0
 const limit = Number(counter.limit) || 0
 const done = counter.completedCount || 0
 if (limit <= 0 || done >= limit) return 0
 const days = Math.max(1, Number(counter.days) || 1)
 const base = counter.perDayCount || computeGoalPerDayCount(limit, days)
 if (isGoalEvenlyDivisible(counter)) {
 return Math.max(1, base)
 }
 const remaining = limit - done
 return Math.min(Math.max(1, base), remaining)
}

/** Batch size shown in the UI — fixed for well-formed goals (50÷5 → always 10). */
export const getGoalRoundDisplayBatchSize = (counter) => {
 if (!counter || counter.type !== "goal") return 0
 if (!isGoalEvenlyDivisible(counter)) return getGoalRoundTarget(counter)
 return Math.max(1, counter.perDayCount || computeGoalPerDayCount(counter.limit || 0, counter.days || 1))
}

/** Daily: perDayCount. Goal: fixed batch size per round (see getGoalRoundTarget). */
export const getCurrentRoundTarget = (counter) => {
 if (!counter) return 0
 if (counter.type === "goal") return getGoalRoundTarget(counter)
 return counter.perDayCount || 0
}

/** Total dhikr counted (lifetime). Prefer persisted completedCount; fallback for old data. */
export const getTotalCounted = (counter) => {
 if (!counter) return 0
 if (typeof counter.completedCount === "number") return counter.completedCount
 const r = counter.rounds || 0
 const p = counter.perDayCount || 0
 return r * p + (counter.currentCount || 0)
}

/** Full batches finished toward the goal (each batch = perDayCount counts, except last may be shorter). */
export const getGoalBatchesCompleted = (counter) => {
 if (!counter || counter.type !== "goal") return counter?.rounds ?? 0
 const total = getTotalCounted(counter)
 const per = Math.max(1, counter.perDayCount || 1)
 if (total <= 0) return 0
 return Math.floor(total / per)
}

export const incrementCounterProgress = (counters, activeCounterId) =>
 counters.map(c => {
 if (c.id !== activeCounterId) return c
 const today = toLocalIsoDateString()
 const next = {
 ...c,
 currentCount: c.currentCount + 1,
 completedCount: (c.completedCount || 0) + 1
 }
 if (c.type === "goal") {
 const dates = [...(c.goalDistinctDates || [])]
 if (!dates.includes(today)) dates.push(today)
 next.goalDistinctDates = dates
 }
 return next
 })

export const decrementCounterProgress = (counters, activeCounterId) =>
 counters.map(c =>
 c.id === activeCounterId && c.currentCount > 0
 ? {
 ...c,
 currentCount: c.currentCount - 1,
 completedCount: Math.max((c.completedCount || 0) - 1, 0)
 }
 : c
 )

export const resetCounterProgress = (counters, activeCounterId) =>
 counters.map(c =>
 c.id === activeCounterId
 ? {
 ...c,
 currentCount: 0,
 rounds: 0,
 completedCount: 0,
 status: "Pending",
 isDisabled: false,
 spentTime: 0,
 spentDays: 0,
 createdAt: toLocalIsoDateString(),
 ...(c.type === "goal"
 ? { goalDistinctDates: [], lastGoalRoundCompleteDate: null }
 : {})
 }
 : c
 )

export const resetCounterTimer = (counters, activeCounterId) =>
 counters.map(c => (c.id === activeCounterId ? { ...c, spentTime: 0 } : c))

export const removeCounterById = (counters, counterId) =>
 counters.filter(c => c.id !== counterId)

export const createHistoryEntry = (now, count, spentTime) => ({
 date: now.toISOString().slice(0, 10),
 count,
 time: now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
 spentTime
})

export const appendHistoryEntry = (counters, counterId, entry) =>
 counters.map(c =>
 c.id === counterId
 ? {
 ...c,
 history: [...c.history, entry]
 }
 : c
 )

export const markCounterInProgress = (counters, activeCounterId) =>
 counters.map(c =>
 c.id === activeCounterId && c.currentCount === 0 && c.status === "Pending"
 ? { ...c, status: "Inprogress" }
 : c
 )

export const completeCounterRound = (counters, activeCounterId, extras = {}) => {
 const { completedCount, status, isDisabled, lastGoalRoundCompleteDate } = extras
 const today = toLocalIsoDateString()

 return counters.map(c => {
 if (c.id !== activeCounterId) return c

 let next = {
 ...c,
 currentCount: 0,
 rounds: c.rounds + 1,
 ...(typeof completedCount === "number" ? { completedCount } : {}),
 ...(typeof status === "string" ? { status } : {}),
 ...(typeof isDisabled === "boolean" ? { isDisabled } : {}),
 totalSpentTime: c.totalSpentTime + c.spentTime,
 spentTime: 0
 }

 if (c.type === "goal") {
 const dates = [...(c.goalDistinctDates || [])]
 if (!dates.includes(today)) dates.push(today)
 next.goalDistinctDates = dates
 next.lastGoalRoundCompleteDate =
 typeof lastGoalRoundCompleteDate === "string" ? lastGoalRoundCompleteDate : today
 }

 return next
 })
}

export const clearCurrentCount = (counters, activeCounterId) =>
 counters.map(c => (c.id === activeCounterId ? { ...c, currentCount: 0 } : c))

/**
 * Daily counters: persistent hint for remaining counts in the current round vs
 * total counts ahead of a single daily round target (extra progress across rounds).
 */
export const getDailyProgressHint = (counter) => {
 if (!counter || counter.type !== "daily" || counter.isDisabled) return null
 const perRound = counter.perDayCount || counter.limit
 if (!perRound) return null

 const totalCounted = counter.rounds * perRound + counter.currentCount
 const ahead = totalCounted - perRound

 if (ahead > 0) {
 const a = ahead === 1 ? "count" : "counts"
 return {
 tone: "ahead",
 message: `You're ${ahead} ${a} ahead of your daily target (${perRound} per round). Total logged: ${totalCounted}.`,
 title: `${ahead} ${a} ahead`,
 subtitle: `Daily target ${perRound} per round · ${totalCounted} total logged`
 }
 }

 const leftInRound = perRound - counter.currentCount
 if (leftInRound > 0) {
 const u = leftInRound === 1 ? "count" : "counts"
 return {
 tone: "remaining",
 message: `${leftInRound} ${u} left to finish this round (${perRound} per round).`,
 title: `${leftInRound} ${u} left`,
 subtitle: `${perRound} per round to complete this round`
 }
 }

 return {
 tone: "complete",
 message: `Daily round target reached (${perRound}).`,
 title: "Round target met",
 subtitle: `${perRound} counts per round for this cycle`
 }
}